import {
    faChevronLeft,
    faChevronRight,
    faRedo,
    faSearchPlus,
    faSpinner,
    faTrash,
    faUndo,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";

import {
    PhotoTitleEditFormFactory,
    PhotoTitleFormData,
    photoTitleEditFormName,
} from "@/mk2/apps/blogs/components/PhotoTitleEditForm";
import { CommentsDesign } from "@/mk2/apps/blogs/constants";
import { Props } from "@/mk2/apps/blogs/containers/BlogsImageDetail/BlogsImageDetailPage";
import Comments from "@/mk2/apps/blogs/containers/Comments/Comments";
import {
    blogsAlbumDetailUrl,
    blogsAlbumPhotoUrl,
    blogsAutoMessagePhotoUrl,
    blogsShortMessagePhotoUrl,
    blogsShortMessageUrl,
} from "@/mk2/apps/blogs/urls";
import { largestAvailablePhotoVersionOfBlogPost, userHasAdminPerm } from "@/mk2/apps/blogs/utils";
import { Emojify } from "@/mk2/components/Emojify";
import { ImgResponsive } from "@/mk2/components/ImgResponsive";
import { Failure } from "@/mk2/components/errors/Failure";
import Complaint from "@/mk2/containers/Complaint/Complaint";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import Complaints from "@/mk2/containers/Complaints/Complaints";
import Like, { LikeType } from "@/mk2/containers/Like/Like";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { cacheLast } from "@/mk2/helpers/cache";
import { url } from "@/mk2/helpers/urls";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import { BlogPostType, PhotoEntity } from "@/mk2/schemas";
import {
    CLICK_HERE_TO_ENTER_DESCRIPTION,
    GO_BACK_TO_GROUP_POST,
    IMAGE_REMOVE_COMPLAINTS,
} from "@/mk/autogenerated/translation";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./BlogsImageDetail.mscss";

interface State {
    isKeyboardEnabled: boolean;
}

const MAX_PHOTO_IMG_HEIGHT = 720;
const MAX_PHOTO_IMG_WIDTH = 624;

class BlogsImageDetail extends React.Component<Props, State> {
    private initialValuesCache = cacheLast();
    private PhotoTitleEditFormCache = cacheLast();

    constructor(props: Props) {
        super(props);
        this.state = {
            isKeyboardEnabled: true,
        };
    }

    public render() {
        const { loadingState } = this.props;

        return (
            <div className={styles.BlogsImageDetail}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderInit={this.renderInitAndSuccess}
                    onRenderSuccess={this.renderInitAndSuccess}
                    onRenderFailure={this.renderFailure}
                    hasData={isSuccess(loadingState)}
                />
            </div>
        );
    }

    public componentDidMount() {
        document.addEventListener("keyup", this.handleKeyUp);
    }

    public componentWillUnmount() {
        document.removeEventListener("keyup", this.handleKeyUp);
    }

    private renderInit = () => {
        return null;
    };

    private renderFailure = () => {
        const {
            routeParams: { postSlug, username },
            errorMessage,
            post,
        } = this.props;

        let blogsPostUrl = blogsShortMessageUrl;
        if (post && post.type === BlogPostType.ALBUM) {
            blogsPostUrl = blogsAlbumDetailUrl;
        }

        return (
            <Failure
                errorMessage={errorMessage}
                onRetry={this.load}
                backButtonUrl={url(blogsPostUrl, { username, postSlug })}
                backButtonLabel={GO_BACK_TO_GROUP_POST}
            />
        );
    };

    private renderInitAndSuccess = () => {
        const {
            actionLoadingState,
            commentsCount,
            currentImage,
            currentIndex,
            isEditingTitle,
            isTiedToPost,
            location,
            routeNavigate,
            post,
            isMobile,
            defaultCanAdd,
            requestUser,
            requestUserPermissions,
        } = this.props;

        const isAdmin =
            post && userHasAdminPerm(!("group" in post) && !post.group, requestUserPermissions);

        const photo: PhotoEntity = currentImage && currentImage.photo;
        if (!photo) {
            return this.renderInit();
        }

        const PhotoTitleEditForm = this.PhotoTitleEditFormCache(
            PhotoTitleEditFormFactory(photoTitleEditFormName(currentImage.id)),
            currentImage.id,
        );

        const showMagnifier = photo.versions.includes("s1600x1600");
        const showRotate = currentImage.canRotate;
        const showDelete = currentImage.canDelete;
        const showControls = showMagnifier || showRotate || showDelete;

        const isActionInProgress = actionLoadingState === LoadingState.LOADING;

        const ratio = photo.width / photo.height;
        let alignedWidth = photo.width;
        let alignedHeight = photo.height;

        if (!isMobile) {
            if (alignedHeight > MAX_PHOTO_IMG_HEIGHT) {
                alignedHeight = MAX_PHOTO_IMG_HEIGHT;
                alignedWidth = alignedHeight * ratio;
                if (alignedWidth > MAX_PHOTO_IMG_WIDTH) {
                    alignedWidth = MAX_PHOTO_IMG_WIDTH;
                    alignedHeight = alignedWidth / ratio;
                }
            }
            if (alignedWidth > MAX_PHOTO_IMG_WIDTH) {
                alignedWidth = MAX_PHOTO_IMG_WIDTH;
                alignedHeight = alignedWidth / ratio;
                if (alignedHeight > MAX_PHOTO_IMG_HEIGHT) {
                    alignedHeight = MAX_PHOTO_IMG_HEIGHT;
                    alignedWidth = alignedHeight * ratio;
                }
            }
        }

        return (
            <>
                <div className={styles.BlogsImageDetail__photo}>
                    <ImgResponsive
                        className={styles.BlogsImageDetail__photo__img}
                        key={photo.id}
                        lazy={false}
                        height={alignedHeight}
                        width={alignedWidth}
                        imgUrl={photoUrl(
                            photo,
                            largestAvailablePhotoVersionOfBlogPost(photo, "s720x720"),
                        )}
                    />

                    <div className={styles.BlogsImageDetail__photo__index}>
                        {currentIndex + 1} / {post.images.length}
                    </div>

                    {post.images.length > 1 && [
                        <div
                            key="prev"
                            className={styles.BlogsImageDetail__photo__prev}
                            onClick={this.handlePrev}
                        >
                            <FontAwesomeIcon
                                icon={faChevronLeft}
                                className={styles.BlogsImageDetail__photo__prev__icon}
                            />
                        </div>,
                        <div
                            key="next"
                            className={styles.BlogsImageDetail__photo__next}
                            onClick={this.handleNext}
                        >
                            <FontAwesomeIcon
                                icon={faChevronRight}
                                className={styles.BlogsImageDetail__photo__next__icon}
                            />
                        </div>,
                    ]}

                    {showControls && (
                        <div className={styles.BlogsImageDetail__photo__controls}>
                            {isActionInProgress ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                                [
                                    showMagnifier && (
                                        <a
                                            data-google-interstitial="false"
                                            key="magnifier"
                                            href={photoUrl(photo, "s1600x1600")}
                                            target="_blank"
                                            rel="noopener"
                                            className={
                                                styles.BlogsImageDetail__photo__controls__control
                                            }
                                        >
                                            <FontAwesomeIcon icon={faSearchPlus} />
                                        </a>
                                    ),
                                    showRotate && (
                                        <div
                                            key="rotate-left"
                                            className={
                                                styles.BlogsImageDetail__photo__controls__control
                                            }
                                            onClick={this.handleRotateLeft}
                                        >
                                            <FontAwesomeIcon icon={faUndo} />
                                        </div>
                                    ),
                                    showRotate && (
                                        <div
                                            key="rotate-right"
                                            className={
                                                styles.BlogsImageDetail__photo__controls__control
                                            }
                                            onClick={this.handleRotateRight}
                                        >
                                            <FontAwesomeIcon icon={faRedo} />
                                        </div>
                                    ),
                                    showDelete && (
                                        <div
                                            key="delete"
                                            className={
                                                styles.BlogsImageDetail__photo__controls__control
                                            }
                                            onClick={this.handleDelete}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </div>
                                    ),
                                ]
                            )}
                        </div>
                    )}
                </div>

                {(currentImage.title || currentImage.canEdit) &&
                    (isEditingTitle ? (
                        <div className={styles.BlogsImageDetail__editingTitle}>
                            <PhotoTitleEditForm
                                initialValues={this.initialValuesCache(
                                    {
                                        title: currentImage.title,
                                    },
                                    currentImage.title,
                                )}
                                onCancel={this.handleTitleCancel}
                                onSave={this.handleTitleSave}
                            />
                        </div>
                    ) : (
                        <div
                            className={cx(
                                styles.BlogsImageDetail__title,
                                currentImage.title && styles["BlogsImageDetail__title--notEditing"],
                                !currentImage.title &&
                                    styles["BlogsImageDetail__title--placeholder"],
                            )}
                            onClick={currentImage.canEdit && this.handleTitleClick}
                        >
                            <Emojify>
                                {currentImage.title
                                    ? currentImage.title
                                    : CLICK_HERE_TO_ENTER_DESCRIPTION}
                            </Emojify>
                        </div>
                    ))}

                <Complaint
                    className={styles.BlogsImageDetail__complaint}
                    entity={ComplaintTargetType.PhotoblogImage}
                    id={currentImage.id}
                    inForum={false}
                    inGroups={post && "group" in post && !!post.group}
                />

                {isAdmin && (
                    <Complaints
                        className={styles.BlogsImageDetail__complaints}
                        id={currentImage.id}
                        entity={ComplaintTargetType.PhotoblogImage}
                        deleteLabel={IMAGE_REMOVE_COMPLAINTS}
                        adminTheme={isAdmin}
                    />
                )}

                <div className={styles.BlogsImageDetail__footer}>
                    <div className={styles.BlogsImageDetail__footer__likes}>
                        <Like
                            entity={isTiedToPost ? "photoblog.post" : "photoblog.image"}
                            id={isTiedToPost ? post.id : currentImage.id}
                            type={LikeType.IconCount}
                            className={styles.BlogsImageDetail__footer__likes__like}
                            location={location}
                        />
                    </div>
                </div>

                <Comments
                    parentPost={post}
                    parentImage={isTiedToPost ? null : currentImage}
                    count={commentsCount}
                    design={CommentsDesign.Normal}
                    isPreloaded
                    location={location}
                    routeNavigate={routeNavigate}
                    onBlur={this.handleCommentsBlur}
                    onFocus={this.handleCommentsFocus}
                    hasBlueToolbar
                    defaultCanAdd={defaultCanAdd}
                    requestUser={requestUser}
                    requestUserPermissions={requestUserPermissions}
                    isMobile={isMobile}
                />
            </>
        );
    };

    private load = () => {
        const {
            routeParams: { postSlug, username, imageId: imageIdStr },
            onLoad,
        } = this.props;
        const imageId = parseInt(imageIdStr, 10);
        onLoad(username, postSlug, imageId);
    };

    private handleTitleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const {
            routeParams: { postSlug, username, imageId: imageIdStr },
            onTitleEdit,
        } = this.props;
        const imageId = parseInt(imageIdStr, 10);
        onTitleEdit(username, postSlug, imageId, true);
    };

    private handleTitleCancel = () => {
        const {
            routeParams: { postSlug, username, imageId: imageIdStr },
            onTitleEdit,
        } = this.props;
        const imageId = parseInt(imageIdStr, 10);
        onTitleEdit(username, postSlug, imageId, false);
    };

    private handleTitleSave = (photoTitleData: PhotoTitleFormData) => {
        const {
            routeParams: { postSlug, username, imageId: imageIdStr },
            onSaveTitle,
        } = this.props;
        const imageId = parseInt(imageIdStr, 10);
        onSaveTitle(username, postSlug, imageId, photoTitleData.title);
    };

    private handleCommentsFocus = () => {
        this.setState({ isKeyboardEnabled: false });
    };

    private handleCommentsBlur = () => {
        this.setState({ isKeyboardEnabled: true });
    };

    private handleKeyUp = (event: KeyboardEvent) => {
        if (!this.state.isKeyboardEnabled || this.props.isEditingTitle) {
            return;
        }

        switch (event.key) {
            case "ArrowLeft":
                this.handlePrev();
                break;
            case "ArrowRight":
                this.handleNext();
                break;
            default:
                return;
        }
        event.preventDefault();
    };

    private handlePrev = () => {
        const { currentIndex, post } = this.props;

        const numImages = post.images.length;
        const newIdx = currentIndex > 0 ? currentIndex - 1 : numImages - 1;
        const newImageId = post.images[newIdx].id;
        this.goToImage(newImageId);
    };

    private handleNext = () => {
        const { currentIndex, post } = this.props;

        const numImages = post.images.length;
        const newIdx = currentIndex < numImages - 1 ? currentIndex + 1 : 0;
        const newImageId = post.images[newIdx].id;
        this.goToImage(newImageId);
    };

    private handleRotateLeft = () => {
        this.rotateImage(90);
    };

    private handleRotateRight = () => {
        this.rotateImage(270);
    };

    private handleDelete = () => {
        const {
            routeParams: { postSlug, username, imageId: imageIdStr },
            onDeleteImage,
        } = this.props;
        const imageId = parseInt(imageIdStr, 10);
        onDeleteImage(username, postSlug, imageId);
    };

    private rotateImage = (angle: number) => {
        const {
            routeParams: { postSlug, username, imageId: imageIdStr },
            onRotateImage,
        } = this.props;
        const imageId = parseInt(imageIdStr, 10);
        onRotateImage(username, postSlug, imageId, angle);
    };

    private goToImage = (imageId) => {
        const {
            routeParams: { postSlug, username },
            post,
        } = this.props;

        const blogsPostPhotoUrl =
            post.type === BlogPostType.ALBUM
                ? blogsAlbumPhotoUrl
                : post.type === BlogPostType.AUTOMATIC_MESSAGE
                  ? blogsAutoMessagePhotoUrl
                  : blogsShortMessagePhotoUrl;

        this.props.routeNavigate(
            url(blogsPostPhotoUrl, {
                username,
                postSlug,
                imageId,
            }),
            { replace: true },
        );
    };
}

export default BlogsImageDetail;
